// Standalone Nav + Footer chrome extracted from _v05_technical.jsx so they
// can be mounted on every interior page of the portal — not just the
// homepage. Visual treatment is byte-identical to the variant, with all
// dynamic copy bindings (t/ts/useDomainCopy) collapsed to their default
// strings. The only configurable input is `brand`.
import React, { useState, useEffect } from 'react';
import { Link } from '../_shared.jsx';

const theme = {
  bg: '#f5f6f7', fg: '#101418', muted: '#5b6470',
  border: '#dfe3e8', accent: '#005ea3', accentFg: '#ffffff',
  inputBg: '#ffffff', radius: 4,
  font: '"Inter", system-ui, sans-serif',
};
const mono = '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update); };
  }, []);
  return isMobile;
};

const HamburgerIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></> :
            <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
  </svg>
);

const navItems = [
  { label: 'So funktioniert es', href: '/so-funktioniert-es' },
  { label: 'Marktanalyse',       href: '/marktanalyse' },
  { label: 'Glossar',            href: '/glossar' },
  { label: 'Wärmepumpen-Rechner', href: '/wärmepumpen-rechner' },
  { label: 'Förderfinder',       href: '/förderung-finder' },
];

export function V05TechnicalNav({ brand = 'Waemo' }) {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return (
    <div style={{
      padding: isMobile ? '10px 16px' : '12px 32px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      borderBottom: `1px solid ${theme.border}`, background: '#fff',
      fontSize: 13,
      fontFamily: theme.font, color: theme.fg,
      position: 'relative',
    }}>
      <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, color: 'inherit', textDecoration: 'none' }}>
        <div style={{ width: 18, height: 18, background: theme.accent, borderRadius: 3 }}/>
        {brand}
        {!isMobile && <span style={{ color: theme.muted, fontWeight: 400, fontFamily: mono, fontSize: 11 }}>{'v3.2 · Stand 04/26'}</span>}
      </Link>
      {!isMobile && (
        <>
          <div style={{ display: 'flex', gap: 22, color: theme.muted }}>
            {navItems.map(l => (
              <Link key={l.label} href={l.href} style={{ color: 'inherit', textDecoration: 'none' }}>{l.label}</Link>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, fontFamily: mono, fontSize: 11, color: theme.muted }}>
            <span>{'● Fachbetriebe online'}</span>
          </div>
        </>
      )}
      {isMobile && (
        <button
          aria-label="Menü"
          onClick={() => setIsMenuOpen(o => !o)}
          style={{ background: 'transparent', border: 'none', color: theme.fg, cursor: 'pointer', padding: 4, display: 'flex' }}
        >
          <HamburgerIcon open={isMenuOpen} />
        </button>
      )}
      {isMobile && isMenuOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: '#fff', borderBottom: `1px solid ${theme.border}`,
          padding: '14px 16px 18px', display: 'flex', flexDirection: 'column', gap: 12,
          zIndex: 50,
        }}>
          {navItems.map(l => (
            <Link key={l.label} href={l.href} style={{ color: theme.fg, textDecoration: 'none', fontSize: 14 }}>{l.label}</Link>
          ))}
          <span style={{ fontFamily: mono, fontSize: 11, color: theme.muted, marginTop: 4 }}>{'● Fachbetriebe online'}</span>
        </div>
      )}
    </div>
  );
}

export function V05TechnicalFooter({ brand = 'Waemo' }) {
  const isMobile = useIsMobile();
  return (
    <footer style={{
      padding: isMobile ? '28px 16px' : '48px 56px',
      background: '#fff',
      borderTop: `1px solid ${theme.border}`,
      fontFamily: theme.font, color: theme.fg,
    }}>
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'baseline',
        gap: isMobile ? 14 : 0,
        fontFamily: mono,
        fontSize: 12,
        color: theme.muted,
        paddingBottom: 22,
        borderBottom: `1px dashed ${theme.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ width: 12, height: 12, background: theme.accent, borderRadius: 2 }}/>
          <span style={{ color: theme.fg, fontWeight: 600 }}>{brand}</span>
          <span>·</span>
          <span>{'v3.2'}</span>
          <span>·</span>
          <span>{'build 04/26'}</span>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 8 : 24,
        }}>
          <Link style={{ color: theme.muted, textDecoration: 'none' }} href="/impressum">{'/impressum'}</Link>
          <Link style={{ color: theme.muted, textDecoration: 'none' }} href="/datenschutz">{'/datenschutz'}</Link>
          <Link style={{ color: theme.muted, textDecoration: 'none' }} href="/agb">{'/agb'}</Link>
          <Link style={{ color: theme.muted, textDecoration: 'none' }} href="/widerruf">{'/widerruf'}</Link>
        </div>
      </div>
      <div style={{
        marginTop: 18,
        fontFamily: mono,
        fontSize: 10,
        color: theme.muted,
        letterSpacing: '0.04em',
      }}>
        {`// (c) 2026 ${brand} — open data · n=29.827 · DIN EN 12831 · VDI 4655`}
      </div>
    </footer>
  );
}
